export interface DynamicDefaultValueTokenOption {
    label: string;
    token: string;
    dataType: string;
    supportedControlTypes: string[];
    supportedListTypes?: string[];
}

export const dynamicDefaultValueTokenOptions: DynamicDefaultValueTokenOption[] = [
    {
        label: 'Current user',
        token: '@me',
        dataType: 'stringvalue',
        supportedControlTypes: ['simpleuserlist', 'list'],
        supportedListTypes: ['USER']
    },
    {
        label: 'Today',
        token: '@today',
        dataType: 'stringvalue',
        supportedControlTypes: ['datetimepicker']
    },
    {
        label: 'Tomorrow',
        token: '@tomorrow',
        dataType: 'stringvalue',
        supportedControlTypes: ['datetimepicker']
    }
];

const unsupportedListSelectTypes = new Set(['MultiDropDown', 'MultiCheckbox']);

export function getSupportedDynamicDefaultValueTokens(field: any): DynamicDefaultValueTokenOption[] {
    return dynamicDefaultValueTokenOptions.filter(option => {
        const controlType = field?.Control?.UniqueId ?? field?.FieldUniqueId ?? field?.FieldType;
        const listType = field?.Control?.Properties?.Parameters?.ListType ?? field?.ListParams?.ListType ?? field?.ListParams?.listType;
        const selectType = field?.Control?.Properties?.Type ?? field?.ListType ?? field?.IsOfType;
        const mappings = field?.Control?.Mappings ?? field?.Mappings ?? [];

        return option.supportedControlTypes.includes(controlType)
            && !unsupportedListSelectTypes.has(selectType)
            && (option.token !== '@me' || controlType !== 'list' || hasCurrentUserMappings(mappings))
            && (!['@today', '@tomorrow'].includes(option.token) || hasDateMappings(mappings))
            && (!option.supportedListTypes || option.supportedListTypes.includes(listType));
    });
}

function hasCurrentUserMappings(mappings: any[]): boolean {
    return mappings.some(mapping => mapping.ControlPropertyName === 'SelectedItemAsGuid')
        && mappings.some(mapping => mapping.ControlPropertyName === 'SelectedText');
}

function hasDateMappings(mappings: any[]): boolean {
    return mappings.some(mapping => mapping.ControlPropertyName === 'Date1')
        && mappings.some(mapping => mapping.ControlPropertyName === 'TimeZoneOffset1');
}

export function getSelectedDynamicDefaultValueToken(defaultDataValue: any): DynamicDefaultValueTokenOption | undefined {
    return dynamicDefaultValueTokenOptions.find(option =>
        defaultDataValue?.Value === option.token && defaultDataValue?.DataType === option.dataType
    );
}

export function buildDynamicDefaultDataValue(field: any, token: string): any {
    const option = dynamicDefaultValueTokenOptions.find(item => item.token === token);
    if (!option) {
        throw new Error(`Unsupported dynamic default value token: ${token}`);
    }

    const fieldPropertyName = field.FieldId ?? field.ParentPropertyName;
    const fieldMappings = field?.Control?.Mappings ?? field?.Mappings ?? [];
    const mappings = buildMappingsForToken(option.token, fieldMappings, fieldPropertyName);

    return {
        DataType: option.dataType,
        OnChangePropertyName: fieldPropertyName,
        Value: option.token,
        Mappings: mappings,
        Whens: [],
        ResetOnUnmatch: false,
        ValueIgnored: true,
        OnChangeOnly: false,
        NewRecordOnly: false
    };
}

function buildMappingsForToken(token: string, fieldMappings: any[], fieldPropertyName: string): any[] {
    switch (token) {
        case '@me':
            return buildCurrentUserMappings(fieldPropertyName);
        case '@today':
        case '@tomorrow':
            return buildDateMappings(fieldMappings, fieldPropertyName);
        default:
            throw new Error(`Unsupported dynamic default value token mapping: ${token}`);
    }
}

function buildCurrentUserMappings(fieldPropertyName: string): any[] {
    return [
        {
            ModelPropertyName: fieldPropertyName,
            ControlPropertyName: 'SelectedItemAsGuid',
            DataPropertyName: 'UserId'
        },
        {
            ModelPropertyName: fieldPropertyName,
            ControlPropertyName: 'SelectedText',
            DataPropertyName: 'Name'
        },
        {
            ModelPropertyName: fieldPropertyName,
            ControlPropertyName: 'SelectedItemAsString',
            DataPropertyName: 'Username'
        }
    ];
}

function buildDateMappings(fieldMappings: any[], fieldPropertyName: string): any[] {
    return [
        buildDateMapping(fieldMappings, fieldPropertyName, 'Date1', 'DateValue'),
        buildDateMapping(fieldMappings, fieldPropertyName, 'Time1', 'TimeValue'),
        buildDateMapping(fieldMappings, fieldPropertyName, 'TimeZoneOffset1', 'TimeZoneOffsetValue')
    ].filter(Boolean);
}

function buildDateMapping(fieldMappings: any[], fieldPropertyName: string, controlPropertyName: string, dataPropertyName: string): any {
    const hasFieldMapping = fieldMappings.some(mapping => mapping.ControlPropertyName === controlPropertyName);
    if (!hasFieldMapping) {
        return undefined;
    }

    return {
        ModelPropertyName: fieldPropertyName,
        ControlPropertyName: controlPropertyName,
        DataPropertyName: dataPropertyName
    };
}
